import React, { createContext, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", {
        email,
        mot_de_passe: password,
      });
      const { access_token, user: userData } = response.data;

      setToken(access_token);
      localStorage.setItem("token", access_token);
      api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

      // Mettre à jour l'utilisateur immédiatement
      setUser(userData);
      toast.success("Connexion réussie !");

      console.log("User data for redirection:", userData); // Debug log

      // Récupérer les données utilisateur les plus récentes pour s'assurer d'avoir password_changed
      try {
        const meResponse = await api.get("/auth/me");
        const currentUserData = meResponse.data;
        console.log("Current user data from /auth/me:", currentUserData);

        // Mettre à jour avec les données les plus récentes
        setUser(currentUserData);

        // Rediriger selon le rôle avec les données les plus récentes
        setTimeout(() => {
          // Vérifier si le mot de passe doit être changé (pour tous les rôles)
          if (!currentUserData?.password_changed) {
            console.log("Redirecting to change password page");
            window.location.href = "/app/change-password";
          } else if (currentUserData?.role === "parent") {
            console.log("Redirecting to parent dashboard");
            window.location.href = "/app/dashboard";
          } else if (
            currentUserData?.role === "admin" ||
            currentUserData?.role === "superadmin"
          ) {
            console.log("Redirecting to admin dashboard");
            window.location.href = "/app/admin";
          } else {
            console.log("No role found, currentUserData:", currentUserData);
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Fallback avec les données de login
        setTimeout(() => {
          if (!userData?.password_changed) {
            console.log("Fallback: Redirecting to change password page");
            window.location.href = "/app/change-password";
          } else if (userData?.role === "parent") {
            console.log("Fallback: Redirecting to parent dashboard");
            window.location.href = "/app/dashboard";
          } else if (
            userData?.role === "admin" ||
            userData?.role === "superadmin"
          ) {
            console.log("Fallback: Redirecting to admin dashboard");
            window.location.href = "/app/admin";
          }
        }, 100);
      }

      return true;
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Email ou mot de passe incorrect");
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    toast.success("Déconnexion réussie");
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    return user.role === requiredRole;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
